/**
 * Règle 2 — décimales : la virgule ET le point sont acceptés partout
 * (1,1 = 1.1). Un champ numérique natif (type="number") fait disparaître la
 * virgule selon la langue du navigateur : « 1,1 » devenait « 11 », « 2,2 »
 * « 22 ». Ce module corrige tous les champs d'un coup.
 */

/** « 1,1 » / « 1.1 » / « 1 234,5 » → 1.1 ; vide ou illisible → null. */
export const parseDecimal = (valeur: unknown): number | null => {
  if (valeur === null || valeur === undefined) return null;
  if (typeof valeur === 'number') return Number.isFinite(valeur) ? valeur : null;
  const brut = String(valeur).trim().replace(/\s/g, '').replace(',', '.');
  if (brut === '' || brut === '-' || brut === '.') return null;
  const n = Number(brut);
  return Number.isFinite(n) ? n : null;
};

/** Comme parseDecimal, mais 0 quand il n'y a rien : pratique pour les totaux. */
export const nombre = (valeur: unknown): number => parseDecimal(valeur) ?? 0;

/** Ce qui est tapé → une saisie propre : chiffres, un seul séparateur (point), signe moins en tête. */
export const nettoyerSaisieDecimale = (texte: string, autoriserNegatif = false): string => {
  let t = texte.replace(/,/g, '.').replace(autoriserNegatif ? /[^\d.-]/g : /[^\d.]/g, '');
  if (autoriserNegatif) t = t.replace(/(?!^)-/g, '');
  const i = t.indexOf('.');
  if (i >= 0) t = t.slice(0, i + 1) + t.slice(i + 1).replace(/\./g, '');
  return t;
};

const ecrireValeur = (el: HTMLInputElement, valeur: string) => {
  // Le setter natif, pour que React voie le changement (son suivi de valeur
  // ignore une affectation directe).
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  setter?.call(el, valeur);
};

/**
 * À appeler UNE fois au démarrage : tous les champs type="number" deviennent
 * des saisies décimales tactiles (clavier numérique, virgule acceptée), et
 * tous les champs inputMode="decimal" reçoivent un point à la place de la
 * virgule avant que l'application ne lise la valeur.
 */
export const installerSaisieDecimale = () => {
  if (typeof document === 'undefined' || (window as any).__saisieDecimaleInstallee) return;
  (window as any).__saisieDecimaleInstallee = true;

  document.addEventListener(
    'focusin',
    (e) => {
      const el = e.target;
      if (!(el instanceof HTMLInputElement) || el.type !== 'number') return;
      // Un champ à valeur négative permise (ajustement de stock) garde le signe.
      const negatif = el.min !== '' && Number(el.min) < 0;
      el.dataset.decimal = negatif ? 'neg' : '1';
      el.type = 'text';
      el.inputMode = 'decimal';
      el.autocomplete = 'off';
    },
    true
  );

  document.addEventListener(
    'input',
    (e) => {
      const el = e.target;
      if (!(el instanceof HTMLInputElement)) return;
      const decimal = el.dataset.decimal || (el.inputMode === 'decimal' ? '1' : '');
      if (!decimal) return;
      const propre = nettoyerSaisieDecimale(el.value, decimal === 'neg');
      if (propre !== el.value) ecrireValeur(el, propre);
    },
    true
  );
};
